import localStorageStore from '../apiStores/application/localStorageStore';
import identityService from './identityService';

const toBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return window.btoa(binary);
};

const fromBase64 = (value) => {
  if (!value) {
    return new Uint8Array();
  }

  let normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  while (normalized.length % 4) {
    normalized += '=';
  }

  const binary = window.atob(normalized);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
};

const getCredentialLookup = (passkeys) => {
  const lookup = new Map();

  passkeys.forEach((passkey) => {
    if (!passkey?.credentialId) {
      return;
    }

    lookup.set(passkey.credentialId, passkey);

    const bytes = fromBase64(passkey.credentialId);
    lookup.set(toBase64(bytes), passkey);
  });

  return lookup;
};

const isSupported = () =>
  typeof window !== 'undefined' && !!window.PublicKeyCredential && !!window.navigator?.credentials;

const ensureSupported = async () => {
  if (!isSupported()) {
    throw new Error('Passkeys are not supported in this browser.');
  }

  if (typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function') {
    const available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    if (!available) {
      throw new Error('A device passkey authenticator is not available.');
    }
  }
};

const passkeyService = {
  isSupported,

  authenticateWithEmail: async (email) => {
    await ensureSupported();

    const discovery = await identityService.discoverPasskeys({ email });
    if (!discovery?.availablePasskeys?.length) {
      throw new Error('No passkeys are registered for this account.');
    }

    const challenge = await identityService.getPasskeyChallenge({ email });
    const availablePasskeys = challenge?.availablePasskeys || discovery.availablePasskeys || [];
    if (!availablePasskeys.length) {
      throw new Error('No compatible passkeys are available for this account.');
    }

    const credential = await window.navigator.credentials.get({
      publicKey: {
        challenge: fromBase64(challenge.challenge),
        timeout: challenge.timeout || 60000,
        userVerification: challenge.requireUserVerification ? 'required' : 'preferred',
        allowCredentials: availablePasskeys
          .filter((passkey) => passkey.credentialId)
          .map((passkey) => ({
            type: 'public-key',
            id: fromBase64(passkey.credentialId),
          })),
      },
    });

    if (!credential) {
      throw new Error('Passkey authentication failed.');
    }

    const passkeyLookup = getCredentialLookup(availablePasskeys);
    const credentialId = toBase64(credential.rawId);
    const matchingPasskey = passkeyLookup.get(credentialId);

    return identityService.authenticateWithPasskey({
      credentialId,
      signature: toBase64(credential.response.signature),
      authenticatorData: toBase64(credential.response.authenticatorData),
      clientDataJSON: toBase64(credential.response.clientDataJSON),
      challenge: challenge.challenge,
      email,
      applicationId: undefined,
      passkeyId: matchingPasskey?.id,
    });
  },
};

export default passkeyService;
